import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  categories,
  templatesByCategory,
  type CatalogueCategory,
  type GenieTemplate,
} from "./data";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface CatalogueViewProps {
  onBack: () => void;
  onPick: (template: GenieTemplate) => void;
}

// The menu of Genies. Sectioned by business function so the page reads as a
// stacked list of categories with their Genies underneath — like a restaurant
// menu. The sticky top jump-nav lets users skip to a section without losing
// the catalogue context.
export const CatalogueView = ({ onBack, onPick }: CatalogueViewProps) => {
  const handleJump = (id: string) => {
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFC] flex flex-col">
      {/* Top bar */}
      <div className="px-4 pt-3 md:px-6 md:pt-4">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="text-genie-text-sub px-2 md:px-3 py-1 md:py-2 text-sm min-w-0 h-8 md:h-9 flex items-center gap-1 md:gap-2 transition-colors duration-200 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
        >
          <ChevronLeft size={16} />
          <span className="hidden md:inline">Back</span>
        </Button>
      </div>

      <div className="flex-1 px-4 md:px-10 pb-16 pt-2 max-w-[1100px] mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8 md:mb-10">
          <GenieIcon
            name="sparkles"
            size={48}
            className="w-12 h-12 md:w-14 md:h-14 mb-3"
          />
          <h1 className="text-xl md:text-3xl font-bold text-genie-text-primary mb-2">
            Hire your next Genie
          </h1>
          <p className="text-xs md:text-sm text-genie-text-sub max-w-md">
            Pick the job you need done. Your Genie ships pre-configured. You add
            your branding and switch it on.
          </p>
        </div>

        {/* Jump-to-section nav */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10 md:mb-12">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => handleJump(c.id)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium bg-white text-genie-text-sub border border-[#E5EAF0] hover:border-[#4E9CFF]/40 hover:text-genie-text-primary transition-colors duration-200"
            >
              <img src={c.iconUrl} alt="" className="w-4 h-4 object-contain" />
              <span>{c.label}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-genie-text-muted">
                {templatesByCategory(c.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* Sections — one per category, stacked vertically */}
        <div className="flex flex-col gap-12 md:gap-16">
          {categories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              templates={templatesByCategory(category.id)}
              onPick={onPick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const CategorySection = ({
  category,
  templates,
  onPick,
}: {
  category: CatalogueCategory;
  templates: GenieTemplate[];
  onPick: (template: GenieTemplate) => void;
}) => {
  if (templates.length === 0) return null;

  return (
    <section id={`section-${category.id}`} className="scroll-mt-6">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-4 md:mb-5 pb-3 border-b border-[#E5EAF0]">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#EBF5FF] to-[#FDF2F8] flex items-center justify-center shrink-0">
          <img
            src={category.iconUrl}
            alt=""
            className="w-5 h-5 object-contain"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base md:text-lg font-bold text-genie-text-primary">
              {category.label}
            </h2>
            <span className="text-[10px] md:text-xs px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-genie-text-muted">
              {templates.length}
            </span>
          </div>
          <p className="text-xs md:text-sm text-genie-text-sub leading-snug">
            {category.description}
          </p>
        </div>
      </div>

      {/* Genie rows */}
      <div className="flex flex-col gap-2">
        {templates.map((template) => (
          <GenieRow
            key={template.slug}
            template={template}
            onClick={() => onPick(template)}
          />
        ))}
      </div>
    </section>
  );
};

const GenieRow = ({
  template,
  onClick,
}: {
  template: GenieTemplate;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className="group text-left rounded-xl bg-white border border-[#E5EAF0] hover:border-transparent hover:shadow-lg hover:shadow-[#4E9CFF]/10 transition-all duration-200 px-3 md:px-4 py-3 md:py-3.5 flex items-center gap-3 md:gap-4 relative overflow-hidden"
  >
    <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#4E9CFF] to-[#FF4DB8] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

    <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-gradient-to-br from-[#EBF5FF] to-[#FDF2F8] flex items-center justify-center overflow-hidden shrink-0">
      <img
        src={template.mascot}
        alt={template.name}
        className="w-full h-full object-cover"
      />
    </div>

    <div className="flex-1 min-w-0">
      <h3 className="text-sm md:text-base font-semibold text-genie-text-primary leading-tight mb-0.5">
        {template.name}
      </h3>
      <p className="text-xs md:text-sm text-genie-text-sub leading-snug line-clamp-1 md:line-clamp-2">
        {template.tagline}
      </p>
    </div>

    <div className="hidden sm:flex items-center gap-3 shrink-0 text-[11px] md:text-xs text-genie-text-muted">
      <span>~{template.setupMinutes} min to launch</span>
      <ChevronRight
        size={16}
        className="text-genie-text-muted group-hover:text-[#4E9CFF] transition-colors duration-200"
      />
    </div>
  </button>
);
